/**
 * Tag model - Core entity for tagging with hierarchy and context support
 *
 * Central table for tag definitions with optional parent-child relationships
 * for taxonomies and category trees.
 */

import { field, SmrtHierarchical, smrt } from '@happyvertical/smrt-core';
import { TenantScoped, tenantId } from '@happyvertical/smrt-tenancy';
import type { TagMetadata, TagOptions } from './types';

@TenantScoped({ mode: 'optional' })
@smrt({
  tableStrategy: 'sti',
  api: { include: ['list', 'get', 'create', 'update', 'delete'] },
  mcp: { include: ['list', 'get', 'create', 'update'] },
  cli: true,
})
export class Tag extends SmrtHierarchical {
  // id: UUID (auto-generated by SmrtObject)
  protected _slug = ''; // Unique identifier
  protected _context = 'global'; // Namespace/grouping

  // Override SmrtObject accessors
  override get slug(): string {
    return this._slug;
  }
  override set slug(value: string) {
    this._slug = value;
  }

  override get context(): string {
    return this._context;
  }
  override set context(value: string) {
    this._context = value;
  }

  @field({ required: true })
  name: string = ''; // Display name

  // parentId inherited from SmrtHierarchical (UUID, nullable). Stores the
  // parent Tag's id (not its slug) — Tag's slug+context natural key remains
  // the public identifier on TagCollection's API, but the FK is now UUID
  // for consistency with Place / Event / Account / Zone.
  level: number = 0; // Hierarchy depth (0 = root)
  description: string = ''; // Optional description
  metadata: string = ''; // JSON metadata stored as text

  // Tenancy
  @tenantId({ nullable: true })
  tenantId: string | null = null;

  // Timestamps
  createdAt: Date = new Date();
  updatedAt: Date = new Date();

  constructor(options: TagOptions = {}) {
    super(options);
    if (options.name) this.name = options.name;
    if (options.parentId !== undefined)
      this.parentId = options.parentId ?? null;
    if (options.slug) this._slug = options.slug;
    if (options.context !== undefined) this._context = options.context;
    if (options.level !== undefined) this.level = options.level;
    if (options.description !== undefined)
      this.description = options.description;

    // Handle metadata - can be object or JSON string
    if (options.metadata !== undefined) {
      if (typeof options.metadata === 'string') {
        this.metadata = options.metadata;
      } else {
        this.metadata = JSON.stringify(options.metadata);
      }
    }
  }

  /**
   * Get metadata as parsed object
   *
   * @returns Parsed metadata object or empty object if no metadata
   */
  getMetadata(): TagMetadata {
    const metadataValue = String(this.metadata || '');
    if (!metadataValue) return {};
    try {
      return JSON.parse(metadataValue);
    } catch {
      return {};
    }
  }

  /**
   * Set metadata from object
   *
   * @param data - Metadata object to store
   */
  setMetadata(data: TagMetadata): void {
    this.metadata = JSON.stringify(data);
  }

  /**
   * Update metadata by merging with existing values
   *
   * @param updates - Partial metadata to merge
   */
  updateMetadata(updates: Partial<TagMetadata>): void {
    const current = this.getMetadata();
    this.setMetadata({ ...current, ...updates });
  }

  // Hierarchy traversal (getParent / getChildren / getAncestors /
  // getDescendants / getHierarchy / moveTo) provided by SmrtHierarchical
  // against the inherited UUID `parentId`. TagCollection wraps these in
  // slug-friendly methods (moveTag / mergeTag / getChildren etc.) so the
  // existing public API surface keeps working for callers that prefer
  // slug references.

  /**
   * Convenience method for slug-based lookup
   *
   * @param slug - The slug to search for
   * @param context - Optional context filter
   * @returns Tag instance or null if not found
   */
  static async getBySlug(
    _slug: string,
    _context?: string,
  ): Promise<Tag | null> {
    // Will be auto-implemented by SMRT
    return null;
  }

  /**
   * Get root tags (no parent) for a context
   *
   * @param context - The context to filter by
   * @returns Array of root tags
   */
  static async getRootTags(_context: string = 'global'): Promise<Tag[]> {
    // Will be auto-implemented by SMRT
    return [];
  }
}
